import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, lastValueFrom, take } from 'rxjs';
import store from 'store2';
import { ToastHelper } from '../../common/toaster.helper';
import { ProductService } from 'src/app/services/product.service';
import {
  ClinicMedicalServiceFormGroupService,
  ClinicMedicalServiceFormGroupMapping
} from 'src/app/services/clinic-medical-service-form-group.service';
import {
  ClinicFormGroupDefaultService,
  ClinicFormGroup
} from 'src/app/services/clinic-form-group-default.service';
import { MenuPermissionFlatDto } from 'src/app/models/clinicParms/operation-item';

interface MedicalServiceWithMapping {
  serviceID: number;
  serviceName: string;
  serviceDescription: string;
  branchID: number;
  groupName?: string;
  mappingID?: number;
  requireForEachService?: boolean;
  hasFormGroup: boolean;
}

@Component({
  selector: 'app-medical-service-formgroup-setup',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './medical-service-formgroup-setup.component.html',
  styleUrl: './medical-service-formgroup-setup.component.scss'
})
export class MedicalServiceFormgroupSetupComponent implements OnInit {
  branchID: number = 0;
  medicalServices: MedicalServiceWithMapping[] = [];
  formGroups: string[] = [];
  isLoading: boolean = false;
  isSaving: boolean = false;

  // Modal properties
  showAssignModal: boolean = false;
  showDeleteModal: boolean = false;
  currentMedicalService: MedicalServiceWithMapping | null = null;
  selectedGroupName: string = '';
  selectedRequireForEachService: boolean = false;

  // Pagination properties
  paginatedMedicalServices: MedicalServiceWithMapping[] = [];
  currentPage: number = 1;
  pageSize: number = 10;
  totalPages: number = 0;

  // Filter properties
  filterText: string = '';
  showOnlyWithForms: boolean = false;

  constructor(
    private _toastr: ToastHelper,
    private _productService: ProductService,
    private _medicalServiceFormGroupService: ClinicMedicalServiceFormGroupService,
    private _clinicFormGroupDefaultService: ClinicFormGroupDefaultService,
    private cdr: ChangeDetectorRef
  ) {}

  userpermissions: MenuPermissionFlatDto[] = JSON.parse(store('userPermissions') || '[]');
  ComponentName: string = 'Default Forms';
  ViewPermission: boolean = false;
  InsertPermission: boolean = false;
  DeletePermission: boolean = false;

  ngOnInit(): void {
    this.ViewPermission = this.userpermissions.find(u => u.cname == this.ComponentName && u.permissionName == 'View')? true : false;
    this.InsertPermission = this.userpermissions.find(u => u.cname == this.ComponentName && u.permissionName == 'Insert')? true : false;
    this.DeletePermission = this.userpermissions.find(u => u.cname == this.ComponentName && u.permissionName == 'Delete')? true : false;
    this.branchID = store('userBranches');
    this.loadData();
  }

  private async loadData(): Promise<void> {
    this.isLoading = true;
    try {
      const [medicalServicesRes, mappingsRes, formGroupsRes] = await lastValueFrom(
        forkJoin([
          this._productService.GetAllMedicalServicesByBranchId(this.branchID),
          this._medicalServiceFormGroupService.getAllMappingsByBranch(this.branchID),
          this._clinicFormGroupDefaultService.GetClinicFormGroup(this.branchID)
        ])
      );
      this.processData(medicalServicesRes, mappingsRes, formGroupsRes);
    } catch (error) {
      console.error('Error loading data:', error);
      this._toastr.showError('Failed to load data');
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  private processData(medicalServicesRes: any, mappingsRes: any, formGroupsRes: ClinicFormGroup[]): void {
    try {
      // Parse medical services
      const services = medicalServicesRes.data || medicalServicesRes;
      console.log('MD services',services)
      console.log('service mapping',mappingsRes)
      console.log('formGroups',formGroupsRes)
      // Parse mappings
      const mappings = mappingsRes as ClinicMedicalServiceFormGroupMapping[];
      console.log('service mapping',mappings)

      // Extract unique group names
      this.formGroups = [...new Set<string>(formGroupsRes.map(x => x.groupName))];

      // Merge medical services with their mappings
      this.medicalServices = services.map((service: any) => {
        const mapping = mappings.find(m => m.serviceID === service.id);
        return {
          serviceID: service.id,
          serviceName: service.serviceName,
          serviceDescription: service.serviceDesc || '',
          branchID: service.branchID,
          groupName: mapping?.groupName,
          mappingID: mapping?.id,
          requireForEachService: mapping?.requireForEachService || false,
          hasFormGroup: !!mapping
        } as MedicalServiceWithMapping;
      });
      console.log('MD mapped services',this.medicalServices);

      this.initializePagination();
      this.isLoading = false;
      this.cdr.markForCheck();
    } catch (error) {
      console.error('Error processing data:', error);
      this._toastr.showError('Failed to process data');
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  // Filter and Pagination Methods
  get filteredMedicalServices(): MedicalServiceWithMapping[] {
    let filtered = this.medicalServices;

    // Apply text filter
    if (this.filterText.trim()) {
      const searchTerm = this.filterText.toLowerCase();
      filtered = filtered.filter(ms =>
        ms.serviceName.toLowerCase().includes(searchTerm) ||
        ms.serviceDescription?.toLowerCase().includes(searchTerm) ||
        ms.groupName?.toLowerCase().includes(searchTerm)
      );
    }

    // Apply "only with forms" filter
    if (this.showOnlyWithForms) {
      filtered = filtered.filter(ms => ms.hasFormGroup);
    }

    return filtered;
  }

  private initializePagination(): void {
    this.currentPage = 1;
    this.updatePagination();
  }

  private updatePagination(): void {
    const filtered = this.filteredMedicalServices;
    this.totalPages = Math.ceil(filtered.length / this.pageSize);
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedMedicalServices = filtered.slice(startIndex, endIndex);
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagination();
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagination();
    }
  }

  onFilterChange(): void {
    this.initializePagination();
  }

  // Modal Methods
  openAssignModal(medicalService: MedicalServiceWithMapping): void {
    this.currentMedicalService = medicalService;
    this.selectedGroupName = medicalService.groupName || '';
    this.selectedRequireForEachService = medicalService.requireForEachService || false;
    this.showAssignModal = true;
  }

  closeAssignModal(): void {
    this.showAssignModal = false;
    this.currentMedicalService = null;
    this.selectedGroupName = '';
    this.selectedRequireForEachService = false;
  }

  openDeleteModal(medicalService: MedicalServiceWithMapping): void {
    this.currentMedicalService = medicalService;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.currentMedicalService = null;
  }

  // Save and Delete Methods
  saveMapping(): void {
    if (!this.currentMedicalService) return;

    if (!this.selectedGroupName.trim()) {
      this._toastr.showError('Please select a form group');
      return;
    }

    this.isSaving = true;
    console.log('service Item:', this.currentMedicalService);
    const mapping: ClinicMedicalServiceFormGroupMapping = {
      id: this.currentMedicalService.mappingID || 0,
      serviceID: this.currentMedicalService.serviceID,
      groupName: this.selectedGroupName,
      branchID: this.branchID,
      requireForEachService: this.selectedRequireForEachService,
      isActive: true,
      createdBy: store('userID'),
      updatedBy: store('userID')
    };
    console.log('serviceGrpForm', mapping);

    this._medicalServiceFormGroupService.saveFormGroupMapping(mapping)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isSaving = false;
          this._toastr.showSuccess('Form group assigned successfully');
          this.closeAssignModal();
          this.loadData();
        },
        error: (err) => {
          this.isSaving = false;
          console.error('Error saving mapping:', err);
          this._toastr.showError('Failed to assign form group');
        }
      });
  }

  confirmDelete(): void {
    if (!this.currentMedicalService) return;

    this.isSaving = true;

    this._medicalServiceFormGroupService.removeFormGroupMapping(this.currentMedicalService.serviceID)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isSaving = false;
          this._toastr.showSuccess('Form group removed successfully');
          this.closeDeleteModal();
          this.loadData();
        },
        error: (err) => {
          this.isSaving = false;
          console.error('Error removing mapping:', err);
          this._toastr.showError('Failed to remove form group');
        }
      });
  }

  // Utility Methods
  get Math() {
    return Math;
  }

  get hasFormGroups(): boolean {
    return this.formGroups && this.formGroups.length > 0;
  }
}
